import { useMemo } from 'react';
import { DeckDesign } from '@/assets/decks/DeckDesign';
import { CardSizes } from './CardFace';

const suitColors: Record<string, string> = {
  '?': 'text-ph-clubs',
  c: 'text-ph-clubs',
  d: 'text-ph-diamonds',
  h: 'text-ph-hearts',
  s: 'text-ph-spades',
  clubs: 'text-ph-clubs',
  diamonds: 'text-ph-diamonds',
  hearts: 'text-ph-hearts',
  spades: 'text-ph-spades',
};

const cardColors: Record<string, string> = {
  '?': 'bg-ph-clubs',
  c: 'bg-ph-clubs',
  d: 'bg-ph-diamonds',
  h: 'bg-ph-hearts',
  s: 'bg-ph-spades',
  clubs: 'bg-ph-clubs',
  diamonds: 'bg-ph-diamonds',
  hearts: 'bg-ph-hearts',
  spades: 'bg-ph-spades',
};

export function useCardFaceStyles(size: CardSizes, suit: string, deckDesign: DeckDesign) {
  const [suitColorClass, backgroundColorClass] = useMemo(() => {
    const suitColor = suitColors[suit.toLowerCase()];
    const cardColor = cardColors[suit.toLowerCase()];
    if (deckDesign === DeckDesign.simpleDeckBlack || deckDesign === DeckDesign.doubleDeckBlack) {
      switch (size) {
        case CardSizes.xxs:
        case CardSizes.xs:
        case CardSizes.sm:
          return ['text-white', `${cardColor} border border-ph-white-30`];
        case CardSizes.md:
        case CardSizes.lg:
        case CardSizes.xl:
        default:
          return ['text-white', `${cardColor} border-2 border-ph-white-30`];
      }
    } else return [suitColor, 'bg-white'];
  }, [suit, deckDesign, size]);

  const [symbolLayoutClasses, bigSuitClasses, smallSuitClasses] = useMemo(() => {
    switch (size) {
      case CardSizes.xxs:
        return ['top-[0.025em] left-[0.0125em]', 'bottom-[0.025em] right-[0.0125em]', 'hidden'];
      case CardSizes.xs:
        return ['top-[0.075em] left-[0.05em]', 'bottom-[0.075em] right-[0.05em]', 'hidden'];
      case CardSizes.sm:
        return ['top-[0.15em] left-[0.1em]', 'bottom-[0.15em] right-[0.1em]', 'hidden'];
      case CardSizes.lg:
        return [
          'top-[0.1666666667em] left-[0.1666666667em]',
          'bottom-[0.1666666667em] right-[0.1666666667em]',
          'top-[2.1666666667em] left-[0.1666666667em]',
        ];
      case CardSizes.xl:
        return [
          'top-[0.1833333333em] left-[0.1833333333em]',
          'bottom-[0.1833333333em] right-[0.1833333333em]',
          'top-[2.383333333em] left-[0.1833333333em]',
        ];
      default:
      case CardSizes.md:
        return ['top-[0.125em] left-[0.125em]', 'bottom-[0.125em] right-[0.125em]', 'top-[1.625em] left-[0.125em]'];
    }
  }, [size]);

  const [symbolSizeClasses, bigSuitSizeClasses, smallSuitSizeClasses] = useMemo(() => {
    switch (size) {
      case CardSizes.xxs:
        return ['w-3 h-3', 'w-3 h-3', ''];
      case CardSizes.xs:
        return ['w-[1.05em] h-[1.05em]', 'w-[1.05em] h-[1.05em]', ''];
      case CardSizes.sm:
        return ['w-[1.4em] h-[1.4em]', 'w-[1.4em] h-[1.4em]', ''];
      case CardSizes.lg:
        return ['w-[2em] h-[2em]', 'w-[3em] h-[3em]', 'w-[2em] h-[2em]'];
      case CardSizes.xl:
        return ['w-[2.2em] h-[2.2em]', 'w-[3.3em] h-[3.3em]', 'w-[2.2em] h-[2.2em]'];
      case CardSizes.md:
      default:
        return ['w-6 h-6', 'w-9 h-9', 'w-6 h-6'];
    }
  }, [size]);

  return {
    suitColorClass,
    backgroundColorClass,
    symbolLayoutClasses,
    bigSuitClasses,
    smallSuitClasses,
    symbolSizeClasses,
    bigSuitSizeClasses,
    smallSuitSizeClasses,
  };
}
